import React from "react";
import { Download, Eye, FileText, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DocumentRecord } from "./documentTypes";
import { richTextToPlainText } from "./richText";

interface Props {
  document: DocumentRecord;
  canDelete: boolean;
  onDelete: (document: DocumentRecord) => void;
  onTogglePin: (document: DocumentRecord) => void;
}

const formatBytes = (value?: number | null) => {
  if (!value) return "No file";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) { size /= 1024; unitIndex++; }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "N/A";

export default function DocumentCard({ document, canDelete, onDelete, onTogglePin }: Props) {
  const navigate = useNavigate();
  const tags = Array.isArray(document.tags) ? document.tags.slice(0, 3) : [];
  const contentPreview =
    document.description ||
    richTextToPlainText(document.content_text || "") ||
    richTextToPlainText(document.extracted_text || "");

  return (
    <article className="group flex h-full flex-col rounded-xl border border-slate-800 bg-slate-900/70 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-950/20 sm:p-5">

      {/* ── Header ── */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300 sm:h-11 sm:w-11">
          <FileText size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 text-sm font-bold leading-snug text-white sm:text-base">
              {document.title}
            </h3>
            {document.is_pinned && (
              <Pin size={13} className="mt-0.5 flex-shrink-0 text-amber-300" />
            )}
          </div>
          <p className="mt-1 line-clamp-1 text-xs text-slate-400">
            {document.subtitle || document.category || "Knowledge base document"}
          </p>
        </div>
      </div>

      {/* ── Content preview ── */}
      <p className="mt-3 line-clamp-3 min-h-[54px] text-xs leading-5 text-slate-300 sm:text-sm sm:leading-6">
        {contentPreview || "No description added yet."}
      </p>

      {/* ── Tags ── */}
      {(document.category || tags.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {document.category && (
            <span className="rounded-full border border-cyan-500/20 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-medium text-cyan-200 sm:text-xs">
              {document.category}
            </span>
          )}
          {tags.map((tag) => (
            <span key={tag} className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300 sm:text-xs">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* ── Meta info ── */}
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-slate-500 sm:text-xs">
        <span className="truncate">📅 {formatDate(document.created_at)}</span>
        <span className="truncate">✏️ {formatDate(document.updated_at)}</span>
        <span className="truncate">👤 {document.uploaded_by || "N/A"}</span>
        <span className="truncate">📄 {document.file_type || "Manual"} · {formatBytes(document.file_size)}</span>
      </div>

      {/* ── Spacer pushes actions to bottom ── */}
      <div className="flex-1" />

      {/* ── Action buttons ── */}
      <div className="mt-4 border-t border-slate-800 pt-3">
        {/* Primary actions row */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => navigate(`/documents/${document.id}`)}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-blue-600 px-2 py-2 text-xs font-semibold text-white hover:bg-blue-500 sm:flex-none sm:px-3"
          >
            <Eye size={13} />
            <span>View</span>
          </button>

          <button
            onClick={() => navigate(`/documents/edit/${document.id}`)}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-700 px-2 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 sm:flex-none sm:px-3"
          >
            <Pencil size={13} />
            <span>Edit</span>
          </button>

          <button
            onClick={() => onTogglePin(document)}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-700 px-2 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 sm:flex-none sm:px-3"
          >
            {document.is_pinned ? <PinOff size={13} /> : <Pin size={13} />}
            <span className="hidden sm:inline">{document.is_pinned ? "Unpin" : "Pin"}</span>
          </button>

          {document.file_url && (
            <button
              onClick={() => window.open(document.file_url || "", "_blank", "noopener,noreferrer")}
              className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-700 px-2 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 sm:flex-none sm:px-3"
            >
              <Download size={13} />
              <span className="hidden sm:inline">Download</span>
            </button>
          )}

          {canDelete && (
            <button
              onClick={() => onDelete(document)}
              className="ml-auto inline-flex items-center justify-center gap-1 rounded-lg border border-rose-500/30 px-2 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/10 sm:px-3"
            >
              <Trash2 size={13} />
              <span className="hidden sm:inline">Delete</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
